import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { type HeadNoyauLine, type HeadNoyauTarget, headNoyauDetail } from '@/lib/session-head';
import { noyauPillInkClass } from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import { noyauOf } from '@matter_extractor/shared';

/**
 * The line under the title of the parcours (refonte-modulaire.md §12.2,
 * « Depuis #218 »): « Noyau retenu [PSG] Producteur Réinventeur · le principal,
 * 56 contre 42 », or « Lu à blanc » on a session that retained none.
 *
 * ## The pill is the one of the list, filled, and a button here
 *
 * The list's pill (`noyau-pill.tsx`) is a link to the session; on the session
 * itself it leads further down, to the block that says how the noyau came
 * (`onOpen`: the route switches to the tab Parcours if need be, scrolls and
 * rings the block). The colour and the ink are the table's, in a style and
 * never a hex written here. The words come from `lib/session-head.ts`.
 */

export interface HeadNoyauLineViewProps {
  readonly line: HeadNoyauLine;
  readonly onOpen?: ((target: HeadNoyauTarget) => void) | undefined;
}

export function HeadNoyauLineView({ line, onOpen }: HeadNoyauLineViewProps) {
  const noyau = noyauOf(line.code);
  const detail = headNoyauDetail(line);
  const trial = line.kind === 'trial';
  return (
    <p
      data-testid={SESSIONS_TEST_IDS.headNoyau}
      data-kind={line.kind}
      data-noyau={line.code}
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600 text-small"
    >
      <span>{line.lead}</span>
      <button
        type="button"
        data-testid={SESSIONS_TEST_IDS.headNoyauPill}
        data-target={line.target}
        aria-label={`${noyau.name} (${line.code}) : ${trial ? 'ouvrir la lecture à blanc' : 'ouvrir la détermination du noyau'}`}
        onClick={() => onOpen?.(line.target)}
        className={cn(
          'inline-flex h-5 min-w-8 cursor-pointer items-center justify-center rounded-full px-2 font-bold text-micro leading-none tracking-wide',
          noyauPillInkClass(line.code),
        )}
        style={{ backgroundColor: noyau.color }}
      >
        {line.code}
      </button>
      <b className="font-semibold text-gray-900">{line.name}</b>
      {detail === null ? null : (
        <span data-testid={SESSIONS_TEST_IDS.headNoyauDetail}>
          {'· '}
          {detail}
        </span>
      )}
    </p>
  );
}
